import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/config";
import {
  collection,
  getDocs,
  addDoc,
  query,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";

function tsToDate(ts) {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
}

function fmtTime(ts) {
  const d = tsToDate(ts);
  if (!d) return "";
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

function roleIcon(role) {
  const icons = {
    President: "👑", Secretary: "📋", Treasurer: "💰",
    Watchman: "🔐", FlatOwner: "🏠", Adyantam: "🔮",
  };
  return icons[role] || "👤";
}

function roleColor(role) {
  const colors = {
    President: "#ffd700", Secretary: "#42a5f5", Treasurer: "#4caf50",
    Watchman: "#ff8a65", FlatOwner: "#8888a8", Adyantam: "#6c63ff",
  };
  return colors[role] || "#8888a8";
}

export default function ChatScreen() {
  const { user, isMC } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState("");
  const [sending, setSending]   = useState(false);
  const [societyName, setSocietyName] = useState("");
  const bottomRef = useRef(null);

  useEffect(() => {
    async function loadSocietyName() {
      try {
        const snap = await getDoc(doc(db, "settings", "init"));
        if (snap.exists()) {
          setSocietyName(snap.data().societyName || "");
        }
      } catch (e) {
        console.error(e);
      }
    }
    loadSocietyName();
  }, []);

  useEffect(() => {
    const q = query(collection(db, "chat"), orderBy("timestamp", "asc"), limit(100));
    const unsub = onSnapshot(q, (snap) => {
      setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function sendMessage() {
    const msg = text.trim();
    if (!msg) return;
    setSending(true);
    try {
      await addDoc(collection(db, "chat"), {
        flatId: user.flatId || "MC",
        senderName: user.name,
        senderRole: user.role,
        roleIcon: roleIcon(user.role),
        message: msg,
        timestamp: serverTimestamp(),
      });
      setText("");
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  // Group messages by date
  function dateKey(ts) {
    const d = tsToDate(ts);
    if (!d) return "";
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return "Today";
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }

  const grouped = [];
  let lastDate = null;
  messages.forEach(m => {
    const dk = dateKey(m.timestamp);
    if (dk !== lastDate) {
      grouped.push({ type: "date", label: dk });
      lastDate = dk;
    }
    grouped.push({ type: "message", ...m });
  });

  const isOwnMessage = (msg) => msg.senderName === user.name && msg.senderRole === user.role;

  return (
    <div style={{
      display: "flex", flexDirection: "column",
      height: "calc(100vh - 120px)", maxWidth: "640px", margin: "0 auto",
    }}>
      {/* Header */}
      <div style={{ padding: "12px 16px", borderBottom: "1px solid #2e2e40", flexShrink: 0 }}>
        <div style={{ fontSize: "15px", fontWeight: "700", color: "#e8e8f0" }}>💬 Society Chat</div>
        <div style={{ fontSize: "11px", color: "#555570", marginTop: "2px" }}>
          {societyName || "Your Society"} — All residents
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
        {grouped.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#555570", fontSize: "13px" }}>
            No messages yet. Say hello! 👋
          </div>
        )}

        {grouped.map((item, i) => {
          if (item.type === "date") {
            return (
              <div key={`date-${i}`} style={{ textAlign: "center", margin: "16px 0 8px" }}>
                <span style={{
                  fontSize: "11px", color: "#555570", background: "#1a1a24",
                  border: "1px solid #2e2e40", borderRadius: "20px", padding: "3px 10px",
                }}>
                  {item.label}
                </span>
              </div>
            );
          }

          const own = isOwnMessage(item);
          const color = roleColor(item.senderRole);

          return (
            <div key={item.id} style={{
              display: "flex", justifyContent: own ? "flex-end" : "flex-start",
              marginBottom: "8px",
            }}>
              {!own && (
                <div style={{
                  width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
                  background: `${color}20`, border: `1px solid ${color}40`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: "14px", marginRight: "8px", marginTop: "2px",
                }}>
                  {roleIcon(item.senderRole)}
                </div>
              )}

              <div style={{ maxWidth: "75%" }}>
                {!own && (
                  <div style={{ display: "flex", gap: "6px", alignItems: "baseline", marginBottom: "3px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "600", color }}>{item.senderName}</span>
                    <span style={{ fontSize: "10px", color: "#555570" }}>· Flat {item.flatId}</span>
                  </div>
                )}

                <div style={{
                  background: own ? "#6c63ff" : "#1a1a24",
                  border: `1px solid ${own ? "#6c63ff" : "#2e2e40"}`,
                  borderRadius: own ? "14px 14px 4px 14px" : "4px 14px 14px 14px",
                  padding: "9px 12px",
                }}>
                  <div style={{
                    fontSize: "13px", color: own ? "#fff" : "#e8e8f0",
                    lineHeight: "1.5", whiteSpace: "pre-wrap", wordBreak: "break-word",
                  }}>
                    {item.message}
                  </div>
                </div>

                <div style={{
                  fontSize: "10px", color: "#555570", marginTop: "3px",
                  textAlign: own ? "right" : "left",
                }}>
                  {fmtTime(item.timestamp)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{
        padding: "10px 16px", borderTop: "1px solid #2e2e40",
        display: "flex", gap: "8px", alignItems: "flex-end", flexShrink: 0,
        background: "#0f0f13",
      }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message… (Enter to send)"
          rows={1}
          style={{
            flex: 1, background: "#1a1a24", border: "1px solid #2e2e40",
            borderRadius: "12px", padding: "10px 14px", color: "#e8e8f0",
            fontSize: "14px", outline: "none", resize: "none", fontFamily: "inherit",
            maxHeight: "100px", overflowY: "auto",
          }}
        />
        <button
          onClick={sendMessage}
          disabled={sending || !text.trim()}
          style={{
            width: "44px", height: "44px", borderRadius: "12px", flexShrink: 0,
            background: (sending || !text.trim()) ? "#2e2e40" : "#6c63ff",
            border: "none", cursor: (sending || !text.trim()) ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px",
          }}
        >
          ↑
        </button>
      </div>
    </div>
  );
}
